import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { STORAGE_KEYS, buildPermissionMap, canDo, canSeeModule } from '../../util/permissions';

const PermissionCtx = createContext({
  can: () => false,
  canSee: () => false,
  refresh: () => {},
});

export function PermissionProvider({ children }) {
  const readFromSession = () => {
    try {
      let lt = sessionStorage.getItem(STORAGE_KEYS.loginType) || '';
      const raw = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.permissions) || '[]');
      const token = sessionStorage.getItem('TOKEN');
      if (!lt && token) {
        lt = 'ADMIN';
      }
      return { lt, pm: buildPermissionMap(raw) };
    } catch {
      return { lt: undefined, pm: buildPermissionMap([]) };
    }
  };

  const [{ lt, pm }, setState] = useState(() => readFromSession());

  const refresh = () => setState(readFromSession());

  const isElevated =
    String(lt || '').toUpperCase() === 'ADMIN' ||
    String(lt || '').toUpperCase() === 'SUPER_ADMIN';

  useEffect(() => {
    const onStorage = (e) => {
      if (
        e.storageArea === sessionStorage &&
        [STORAGE_KEYS.loginType, STORAGE_KEYS.permissions].includes(e.key || '')
      ) {
        refresh();
      }
    };
    window.addEventListener('storage', onStorage);

    // Sync permissions on mount for sub-admins
    const token = sessionStorage.getItem('TOKEN');
    const loginType = sessionStorage.getItem(STORAGE_KEYS.loginType);

    if (token && loginType === 'staff') {
      const normalize = (perms) => {
        return (perms || [])
          .map((p) => ({
            module: p.module,
            actions: [...(p.actions || [])].sort(),
          }))
          .sort((a, b) => a.module.localeCompare(b.module));
      };

      import('../../util/api').then(({ apiInstanceFetch }) => {
        apiInstanceFetch.get('admin/profile').then((res) => {
          if (res.status && res.admin?.role?.permissions) {
            const newPerms = res.admin.role.permissions;
            const currentRaw = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.permissions) || '[]');
            
            const normalizedNew = normalize(newPerms);
            const normalizedCurrent = normalize(currentRaw);

            if (JSON.stringify(normalizedNew) !== JSON.stringify(normalizedCurrent)) {
               sessionStorage.setItem(STORAGE_KEYS.permissions, JSON.stringify(newPerms));
               refresh();
            }
          }
        }).catch(err => console.error('Permission sync failed:', err));
      });
    }

    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({
      loginType: lt,
      permissionMap: pm,
      can: (moduleName, action) =>
        isElevated ? true : canDo(lt, pm, moduleName, action),
      canSee: (moduleName) => (isElevated ? true : canSeeModule(lt, pm, moduleName)),
      refresh,
    }),
    [lt, pm, isElevated]
  );

  return (
    <PermissionCtx.Provider value={value}>{children}</PermissionCtx.Provider>
  );
}

export const usePermission = () => useContext(PermissionCtx);
